import React, { useEffect, useState } from 'react';
import { Responsive, WidthProvider } from 'react-grid-layout';

import { Button, Button as EditorButton } from '~components/generic/editor/Button/Button';
import DraggableModuleContainer from '~components/generic/module/DraggableModuleContainer';
import { DeleteIcon, PlusIcon } from '~components/icons';
import { IMoodboardGridProps } from '~modules/moodboard/ModuleMoodboard';
import { saveEntityWorker, saveMoodboardEntity } from '~store/thunks/entity';
const ResponsiveReactGridLayout = WidthProvider(Responsive);
import useAppDispatch from '~hooks/useAppDispatch';

const CustomMoodboard: React.FC<IMoodboardGridProps> = props => {
  const {
    data,
    parentType,
    parentContext,
    fieldName,
    siblingsLength,
    stable,
    gridData,
    onChangeGridData,
    editing,
    origData,
    onDeleteChild,
    onAddNewChild,
  } = props;
  const dispatch = useAppDispatch();

  const [layout, setLayout] = useState(gridData);
  const [breakpoint, setBreakpoint] = useState<string>('lg');

  useEffect(() => {
    const updatedItems = gridData.map((item, index) => ({
      ...item,
      id: data[index],
    }));

    setLayout(updatedItems);
  }, [gridData, data]);

  const getNearestAspectRatio = (width, height) => {
    // List of aspect ratios
    const aspectRatios = [
      { w: 1, h: 1 },
      { w: 2, h: 1 },
      { w: 3, h: 4 },
      { w: 4, h: 1 },
      { w: 4, h: 3 },
      { w: 9, h: 16 },
      { w: 16, h: 9 },
      { w: 1, h: 2 },
      { w: 2, h: 3 },
      { w: 3, h: 2 },
      { w: 5, h: 3 },
      { w: 5, h: 4 },
    ];

    // Calculate the actual aspect ratio
    const actualRatio = width / height;

    // Calculate the difference between the actual ratio and the predefined ratios
    let minDifference = Infinity;
    let nearestRatio;

    for (const ratio of aspectRatios) {
      const difference = Math.abs(actualRatio - ratio.w / ratio.h);
      if (difference < minDifference) {
        minDifference = difference;
        nearestRatio = ratio;
      }
    }

    // Return the nearest aspect ratio in "W:H" format
    return `${nearestRatio.w}:${nearestRatio.h}`;
  };

  const handleResize = (l, oldItem, newItem) => {
    const nearestAspectRatio = getNearestAspectRatio(newItem.w, newItem.h);
    const indexToUpdate = layout.findIndex(item => item.i === newItem.i);

    if (indexToUpdate !== -1) {
      // Create a copy of the layout array with the updated item
      const updatedItems = [...layout];
      updatedItems[indexToUpdate] = {
        ...updatedItems[indexToUpdate],
        ...newItem,
        aspectRatio: nearestAspectRatio,
      };

      setLayout(updatedItems);
      onChangeGridData(updatedItems); // Notify the change to your callback function
    }
  };

  const handleDragStop = l => {
    const updatedItems = layout.map((item, index) => {
      const newItem = { ...item };
      newItem.x = l[index].x;
      newItem.y = l[index].y;
      return newItem;
    });

    setLayout(updatedItems);
    onChangeGridData(updatedItems);
  };

  const handleAddItem = async () => {
    onAddNewChild({
      id: origData,
      fieldName: 'relationships.field_images.data',
      templateType: 'paragraph--moodboard_image',
    });
    await dispatch(saveEntityWorker({ entityId: parentContext }));

    // Find the maximum y value in the current layout
    let maxY = 0;
    layout.forEach(item => {
      maxY = Math.max(maxY, item.y + item.h);
    });

    const fieldImages = await dispatch(saveMoodboardEntity(parentContext));

    // Get the last ID from the data array
    if (Array.isArray(fieldImages)) {
      const lastChild = fieldImages[fieldImages.length - 1];
      // Create a new item with the next number
      const newItem = {
        w: 3,
        h: 3,
        x: 0,
        y: maxY,
        i: lastChild.id,
        aspectRatio: '1:1',
        id: lastChild.id,
      };

      onChangeGridData([...layout, newItem]);
      dispatch(saveEntityWorker({ entityId: parentContext }));
    } else {
      console.error('fieldImages is not an array', fieldImages);
    }
  };

  const handleDeleteItem = childId => {
    onDeleteChild({
      childId: childId,
      id: parentContext,
      fieldName,
    });

    const updatedItems = layout.filter(i => i.id !== childId);

    onChangeGridData(updatedItems);
    dispatch(saveEntityWorker({ entityId: parentContext }));
  };

  const onBreakpointChange = breakpoint => {
    setBreakpoint(breakpoint);
  };

  const calculateRowHeight = () => {
    const containerWidth = window.innerWidth;
    return containerWidth / 12;
  };

  return (
    <div>
      {editing && <Button label={'Add Item'} title={'Add Item'} preIcon={<PlusIcon />} onClick={handleAddItem} />}

      <ResponsiveReactGridLayout
        rowHeight={calculateRowHeight()}
        isResizable={editing}
        isDraggable={editing}
        draggableCancel=".no-drag"
        onDragStop={handleDragStop}
        onResize={handleResize}
        className={editing ? 'layout-resizable' : 'layout'}
        layouts={{ lg: layout }}
        cols={{ lg: 12, md: 12, sm: 12, xs: 12, xxs: 12 }}
        breakpoint={breakpoint}
        onBreakpointChange={onBreakpointChange}
      >
        {layout.map(item => {
          return (
            <div key={item.i}>
              {editing && (
                <div className="justify-end flex no-drag">
                  <EditorButton
                    icon={<DeleteIcon />}
                    onClick={() => {
                      handleDeleteItem(item.id);
                    }}
                  />
                </div>
              )}
              <DraggableModuleContainer
                parentIsStable={stable}
                index={Number(item.id)}
                entityId={item.id}
                proportions={item.aspectRatio}
                customMoodboard={true}
                parentContext={parentContext}
                parentType={parentType}
                fieldName={fieldName}
                siblingsLength={siblingsLength}
                canMove={false}
                canDelete={false}
                editablePictureClassName={{
                  'u-overflow-hidden': true,
                  relative: true,
                  'u-full-height': true,
                  rounded: true,
                }}
                containerClassName={{ 'u-full-height': true, relative: true }}
              />
            </div>
          );
        })}
      </ResponsiveReactGridLayout>
    </div>
  );
};

export default CustomMoodboard;
